import Phaser from 'phaser';
import { COLORS, GAME_HEIGHT, GAME_WIDTH, SAFE_AREA, UI_SIZES } from '../../config';
import { Button } from '../../ui/Button';

export interface TrackScaffoldConfig {
  trackPadding: number;
  laneDividerBottomY: number;
  laneDividerAlpha?: number;
  laneDividerWidth?: number;
}

export interface ParallaxScaffoldConfig {
  trackPadding: number;
  lineSpacing: number;
  lineWidth: number;
  lineAlpha: number;
  lineSpeedRatio: number;
}

export interface PauseButtonConfig {
  isPaused: () => boolean;
  onToggle: () => void;
}

export function createGameplayBackground(scene: Phaser.Scene): void {
  scene.add.rectangle(
    GAME_WIDTH / 2,
    GAME_HEIGHT / 2,
    GAME_WIDTH,
    GAME_HEIGHT,
    COLORS.bgGameplay
  );

  const atmosphere = scene.add.graphics();
  atmosphere.fillStyle(0x0A2C3C, 0.34);
  atmosphere.fillCircle(GAME_WIDTH + 50, SAFE_AREA.top + 90, 210);
  atmosphere.fillStyle(0x07131F, 0.75);
  atmosphere.fillCircle(-60, GAME_HEIGHT - 60, 230);
}

export function createGameplayTrack(scene: Phaser.Scene, config: TrackScaffoldConfig): void {
  const trackWidth = GAME_WIDTH - config.trackPadding * 2;
  const trackHeight = GAME_HEIGHT - SAFE_AREA.top - SAFE_AREA.bottom;
  const trackY = SAFE_AREA.top + trackHeight / 2;

  scene.add.rectangle(
    GAME_WIDTH / 2,
    trackY,
    trackWidth,
    trackHeight,
    COLORS.bgTrack
  );

  const rails = scene.add.graphics();
  rails.lineStyle(3, 0x244E62, 0.82);
  rails.lineBetween(config.trackPadding, SAFE_AREA.top, config.trackPadding, GAME_HEIGHT - SAFE_AREA.bottom);
  rails.lineBetween(
    GAME_WIDTH - config.trackPadding,
    SAFE_AREA.top,
    GAME_WIDTH - config.trackPadding,
    GAME_HEIGHT - SAFE_AREA.bottom
  );
  rails.lineStyle(1, 0x00D4FF, 0.28);
  rails.lineBetween(config.trackPadding + 5, SAFE_AREA.top, config.trackPadding + 5, GAME_HEIGHT - SAFE_AREA.bottom);
  rails.lineBetween(
    GAME_WIDTH - config.trackPadding - 5,
    SAFE_AREA.top,
    GAME_WIDTH - config.trackPadding - 5,
    GAME_HEIGHT - SAFE_AREA.bottom
  );

  const divider = scene.add.graphics();
  const dividerWidth = config.laneDividerWidth ?? 2;
  const dividerAlpha = config.laneDividerAlpha ?? 0.8;
  for (let y = SAFE_AREA.top + 10; y < config.laneDividerBottomY; y += 28) {
    divider.lineStyle(dividerWidth, COLORS.grid, dividerAlpha * 0.58);
    divider.lineBetween(GAME_WIDTH / 2, y, GAME_WIDTH / 2, Math.min(y + 13, config.laneDividerBottomY));
  }

  const topBand = scene.add.graphics();
  topBand.fillStyle(0x02070D, 0.76);
  topBand.fillRect(config.trackPadding, SAFE_AREA.top, trackWidth, 4);
  topBand.fillStyle(0x00D4FF, 0.42);
  topBand.fillRect(GAME_WIDTH / 2 - 34, SAFE_AREA.top, 68, 2);
}

export function createParallaxLines(
  scene: Phaser.Scene,
  config: ParallaxScaffoldConfig
): Phaser.GameObjects.Line[] {
  const lines: Phaser.GameObjects.Line[] = [];
  const lineCount = Math.ceil(GAME_HEIGHT / config.lineSpacing) + 2;

  for (let i = 0; i < lineCount; i += 1) {
    const y = i * config.lineSpacing;
    const line = scene.add.line(
      0,
      0,
      config.trackPadding,
      y,
      GAME_WIDTH - config.trackPadding,
      y,
      COLORS.grid,
      config.lineAlpha
    );
    line.setLineWidth(config.lineWidth);
    line.setOrigin(0, 0);
    lines.push(line);
  }

  return lines;
}

export function updateParallaxLines(
  lines: Phaser.GameObjects.Line[],
  deltaMs: number,
  scrollSpeed: number,
  config: Pick<ParallaxScaffoldConfig, 'lineSpacing' | 'lineSpeedRatio'>
): void {
  const movement = (scrollSpeed * config.lineSpeedRatio * deltaMs) / 1000;

  for (const line of lines) {
    line.y += movement;
    if (line.y > GAME_HEIGHT + config.lineSpacing) {
      line.y -= lines.length * config.lineSpacing;
    }
  }
}

export function clampToTrackBounds(
  x: number,
  leftBound: number,
  rightBound: number,
  entityWidth: number = 0
): number {
  const halfWidth = entityWidth / 2;
  return Phaser.Math.Clamp(x, leftBound + halfWidth, rightBound - halfWidth);
}

export function setPauseButtonIcon(button: Button, isPaused: boolean): void {
  button.setIcon(isPaused ? 'icon-play' : 'icon-pause');
}

export function createPauseIconButton(scene: Phaser.Scene, config: PauseButtonConfig): Button {
  const button = new Button(scene, {
    x: GAME_WIDTH - UI_SIZES.iconButton / 2 - 16,
    y: SAFE_AREA.top + UI_SIZES.iconButton / 2 + 8,
    icon: 'icon-pause',
    variant: 'icon',
    onClick: () => config.onToggle(),
  });

  setPauseButtonIcon(button, config.isPaused());
  return button;
}
